import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import styles from './FolderContentPopup.module.css';

// Importando os componentes e hooks necessários
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// --- 1. IMPORTAR OS PLUGINS NECESSÁRIOS ---
import Video from "yet-another-react-lightbox/plugins/video"; // Plugin para vídeo
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import toast, { Toaster } from 'react-hot-toast';
import { 
    FaTimes, FaUpload, FaFilePdf, FaFileImage, 
    FaFileVideo, FaEye, FaDownload, FaTrash,
    FaFileAlt, FaSpinner
} from 'react-icons/fa';
import { API_BASE_URL } from '../../../config';

const FolderContentPopup = ({ isOpen, onClose, folderName, folderIcon, imovelId }) => {
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const fileInputRef = useRef(null);

    const lightboxSlides = files
        .filter(file => file.file_type.startsWith('image/') || file.file_type.startsWith('video/'))
        .map(file => ({
            type: file.file_type.startsWith('video/') ? 'video' : 'image',
            src: `${API_BASE_URL}${file.file_path}`,
            sources: file.file_type.startsWith('video/') ? [
                { src: `${API_BASE_URL}${file.file_path}`, type: file.file_type }
            ] : null,
            download: `${API_BASE_URL}${file.file_path}`
        }));

    const fetchFiles = useCallback(async () => {
        if (!imovelId) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE_URL}/api/imoveis/${imovelId}/arquivos/${folderName}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setFiles(response.data || []);
        } catch (err) {
            toast.error('Falha ao buscar arquivos.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [imovelId, folderName]);

    useEffect(() => {
        if (isOpen) {
            fetchFiles();
        }
    }, [isOpen, fetchFiles]);

    if (!isOpen) return null;

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };
    
    // <-- MUDANÇA AQUI: Função ajustada para múltiplos arquivos -->
    const handleFileChange = async (event) => {
        const selectedFiles = event.target.files; // Pega TODOS os arquivos selecionados
        if (!selectedFiles || selectedFiles.length === 0 || !imovelId) return;

        const formData = new FormData();
        // Itera sobre os arquivos e anexa todos ao campo 'files' (plural)
        for (let i = 0; i < selectedFiles.length; i++) {
            formData.append('files', selectedFiles[i]);
        }
        
        const token = localStorage.getItem('token');
        const uploadPromise = axios.post(
            `${API_BASE_URL}/api/imoveis/${imovelId}/arquivos/${folderName}`,
            formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        toast.promise(uploadPromise, {
            loading: 'Enviando arquivos...',
            success: 'Arquivos enviados com sucesso!',
            error: 'Falha no upload de um ou mais arquivos.',
        });

        try {
            await uploadPromise;
            fetchFiles(); // Atualiza a lista após o sucesso
        } catch (error) {
            console.error(error);
        } finally {
            event.target.value = null; // Limpa o input para permitir o re-upload dos mesmos arquivos
        }
    };

    const handleDelete = async (fileId) => {
        if (!window.confirm("Tem certeza que deseja excluir este arquivo?")) return;
        
        const token = localStorage.getItem('token');
        const deletePromise = axios.delete(`${API_BASE_URL}/api/arquivos/${fileId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        toast.promise(deletePromise, {
            loading: 'Excluindo arquivo...',
            success: 'Arquivo excluído com sucesso!',
            error: 'Falha ao excluir o arquivo.',
        });
        
        try {
            await deletePromise;
            setFiles(files.filter(f => f.id !== fileId));
        } catch (error) {
            console.error(error);
        }
    };

    const handleDownload = async (file) => {
        const token = localStorage.getItem('token');
        const downloadPromise = axios.get(`${API_BASE_URL}${file.file_path}`, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        toast.promise(downloadPromise, {
            loading: 'Preparando download...',
            success: 'Download iniciado!',
            error: 'Falha ao baixar o arquivo.',
        });
        
        try {
            const response = await downloadPromise;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.file_name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Erro ao baixar o arquivo:", error);
        }
    };
    
    const handlePreview = (file) => {
        const fileIndexInLightbox = lightboxSlides.findIndex(slide => slide.src === `${API_BASE_URL}${file.file_path}`);

        if (fileIndexInLightbox !== -1) {
            setLightboxIndex(fileIndexInLightbox);
        } else if (file.file_type === 'application/pdf') {
            window.open(`${API_BASE_URL}${file.file_path}`, '_blank');
        } else {
            toast.error('Pré-visualização não disponível para este tipo de arquivo.');
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) return <FaFileImage className={styles.fileIcon} style={{ color: '#3b82f6' }} />;
        if (fileType.startsWith('video/')) return <FaFileVideo className={styles.fileIcon} style={{ color: '#ef4444' }} />;
        if (fileType === 'application/pdf') return <FaFilePdf className={styles.fileIcon} style={{ color: '#f97316' }} />;
        return <FaFileAlt className={styles.fileIcon} />;
    };

    return (
        <>
            <Toaster position="top-center" reverseOrder={false} />

            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.modalTitle}>{folderIcon} {folderName}</h2>
                        <button onClick={onClose} className={styles.closeButton}><FaTimes /></button>
                    </div>

                    <div className={styles.uploadSection}>
                        {/* <-- MUDANÇA AQUI: Adiciona a propriedade 'multiple' --> */}
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} disabled={isLoading} multiple />
                        <button className={styles.uploadButton} onClick={handleUploadClick} disabled={isLoading}>
                            {isLoading ? <FaSpinner className={styles.spin} /> : <FaUpload />} 
                            {isLoading ? 'Carregando...' : 'Fazer Upload de Arquivos'}
                        </button>
                    </div>

                    <div className={styles.fileListContainer}>
                        {isLoading && files.length === 0 && <p className={styles.centeredMessage}>Carregando arquivos...</p>}
                        {!isLoading && files.length === 0 && <p className={styles.centeredMessage}>Nenhum arquivo encontrado nesta pasta.</p>}
                        {files.map((file) => (
                            <div key={file.id} className={styles.fileItem}>
                                {getFileIcon(file.file_type)}
                                <div className={styles.fileInfo}>
                                    <span className={styles.fileName}>{file.file_name}</span>
                                    <span className={styles.fileSize}>{file.file_size_kb} KB</span>
                                </div>
                                <div className={styles.fileActions}>
                                    <button onClick={() => handlePreview(file)} className={`${styles.actionButton} ${styles.view}`} title="Visualizar"><FaEye /></button>
                                    <button onClick={() => handleDownload(file)} className={`${styles.actionButton} ${styles.download}`} title="Baixar"><FaDownload /></button>
                                    <button onClick={() => handleDelete(file.id)} className={`${styles.actionButton} ${styles.delete}`} title="Excluir"><FaTrash /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
            <Lightbox
                open={lightboxIndex > -1}
                close={() => setLightboxIndex(-1)}
                index={lightboxIndex}
                slides={lightboxSlides}
                plugins={[Video, Thumbnails, Zoom]}
                thumbnails={{
                    position: "bottom",
                    height: 80,
                    width: 120,
                    padding: 4,
                }}
            />
        </>
    );
};

export default FolderContentPopup;